import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ArrowLeft, Building2, Users, FileClock, ExternalLink, Landmark, BadgeCheck, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";

function PendingBadge() {
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full"><FileClock className="w-3 h-3" /> Verification pending</span>;
}

export default function StateProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/compliance/states/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-blue-950">State not found</h1>
      <Button asChild className="mt-6 bg-blue-900 text-white"><Link to="/compliance">Back to Navigator</Link></Button>
    </div>
  );
  if (!data) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const { state, authorities, care_settings, worker_roles, categories, has_verified_data, last_reviewed, source_categories } = data;

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/compliance" className="inline-flex items-center gap-1.5 text-sm text-teal-300 mb-5 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> All states</Link>
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {state.region} · {state.abbr}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">{state.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sm text-blue-100/80"><CalendarClock className="w-4 h-4 text-teal-400" /> Last reviewed: {last_reviewed || "—"}</span>
            {!has_verified_data && <PendingBadge />}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[1fr_320px] gap-10">
        <div className="space-y-8">
          {!has_verified_data && (
            <div className="flex items-start gap-2 bg-amber-50 text-amber-900 rounded-xl px-4 py-3 text-sm border border-amber-200">
              <FileClock className="w-5 h-5 shrink-0 mt-0.5" />
              <span>Verified regulatory records for {state.name} haven't been entered yet. Every requirement below shows <strong>verification pending</strong> until confirmed against an official source — we never fabricate requirements.</span>
            </div>
          )}

          <div>
            <h2 className="font-display text-2xl font-bold text-blue-950 mb-4">Requirements by category</h2>
            <div className="space-y-2.5" data-testid="state-categories">
              {categories.map((c) => (
                <div key={c.id} data-testid={`state-cat-${c.id}`} className="bg-white rounded-xl border border-blue-900/5 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-blue-950 text-sm">{c.name}</p>
                    {c.status === "verified" ? <span className="text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full">{c.requirements.length} verified</span> : <PendingBadge />}
                  </div>
                  {c.status === "verified" && (
                    <ul className="mt-3 space-y-2">
                      {c.requirements.map((r, i) => (
                        <li key={r.id || i} className="text-sm text-slate-600 border-l-2 border-teal-500 pl-3">
                          <p className="text-blue-950">{r.requirement}</p>
                          {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-teal-700 underline inline-flex items-center gap-1">View source <ExternalLink className="w-3 h-3" /></a>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display font-bold text-blue-950 mb-3 flex items-center gap-2"><Building2 className="w-5 h-5 text-teal-600" /> Care settings</h3>
              <div className="flex flex-wrap gap-2">{care_settings.map((s) => <span key={s.id} className="text-xs bg-white border border-blue-900/10 text-blue-900 px-3 py-1.5 rounded-full">{s.name}</span>)}</div>
            </div>
            <div>
              <h3 className="font-display font-bold text-blue-950 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Worker roles</h3>
              <div className="flex flex-wrap gap-2">{worker_roles.map((r) => <span key={r.id} className="text-xs bg-white border border-blue-900/10 text-blue-900 px-3 py-1.5 rounded-full">{r.name}</span>)}</div>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-white rounded-2xl border border-blue-900/5 p-5 shadow-sm">
            <h3 className="font-display font-bold text-blue-950 mb-3 flex items-center gap-2"><Landmark className="w-5 h-5 text-teal-600" /> Regulating authorities</h3>
            <ul className="space-y-2.5" data-testid="state-authorities">
              {authorities.map((a, i) => (
                <li key={i} className="text-sm">
                  <p className="text-blue-950 font-medium">{a.name}</p>
                  <p className="text-xs text-slate-400">{a.category}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl border border-blue-900/5 p-5 shadow-sm">
            <h3 className="font-display font-bold text-blue-950 mb-3 flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-teal-600" /> Trusted source center</h3>
            <p className="text-xs text-slate-500 mb-3">Every verified requirement is traceable to one of these official source categories.</p>
            <ul className="space-y-1.5 text-sm text-slate-600">{source_categories.map((s) => <li key={s} className="flex gap-2"><span className="text-teal-500">•</span>{s}</li>)}</ul>
          </div>
          <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white"><Link to="/compliance">Open Navigator for {state.abbr}</Link></Button>
        </aside>
      </div>
    </div>
  );
}
